import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PriceItem } from '@/types';
import { tokenColor } from '@/utils/theme';

export type ChartView = 'day' | 'week' | 'month';

interface Point {
  label: string;
  value: number;
}

function buildSeries(item: PriceItem, view: ChartView): Point[] {
  const trend = item.trend;
  const last = trend[trend.length - 1];
  const first = trend[0];

  if (view === 'day') {
    return trend.map((value, index) => ({
      label: `${String(9 + (index % 12)).padStart(2, '0')}:00`,
      value,
    }));
  }

  if (view === 'week') {
    const labels = ['周一', '周二', '周三', '周四', '周五'];
    return labels.map((label, index) => {
      const source = trend[Math.min(index * 3, trend.length - 1)];
      return { label, value: Number(source.toFixed(2)) };
    });
  }

  const labels = ['09/12', '09/18', '09/24', '09/30', '10/02', '10/04', '10/06', '10/08', '10/09', '10/10', '10/11', '10/12'];
  const delta = (last - first) * 0.18;
  return labels.map((label, index) => {
    const ratio = index / (labels.length - 1);
    const value = first + (last - first) * ratio + Math.sin(index / 1.5) * delta;
    return { label, value: Number(value.toFixed(2)) };
  });
}

interface PriceChartProps {
  item: PriceItem;
  view: ChartView;
}

export default function PriceChart({ item, view }: PriceChartProps) {
  const data = useMemo(() => buildSeries(item, view), [item, view]);
  const up = item.change >= 0;
  const color = up ? tokenColor('accent-500') : tokenColor('secondary-500');

  const values = data.map((point) => point.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.15 || 1;

  return (
    <div className="h-[280px] w-full md:h-[340px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="4 4" stroke="oklch(var(--background-300))" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12, fill: 'oklch(var(--foreground-500))' }}
            axisLine={{ stroke: 'oklch(var(--background-300))' }}
            tickLine={false}
          />
          <YAxis
            domain={[Number((min - pad).toFixed(2)), Number((max + pad).toFixed(2))]}
            tick={{ fontSize: 12, fill: 'oklch(var(--foreground-500))' }}
            axisLine={false}
            tickLine={false}
            width={64}
            tickFormatter={(value: number) => value.toFixed(2)}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: '1px solid oklch(var(--background-300))',
              background: 'oklch(var(--background-50))',
              fontSize: 12,
            }}
            labelStyle={{ color: 'oklch(var(--foreground-700))' }}
            formatter={(value: number) => [value.toFixed(2), item.unit]}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}